import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableWithoutFeedback,
  Pressable,
  TextInput,
  Text,
  Modal,
} from 'react-native';
import { isWeb, Platform } from '../util/platform';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { Wrap, Box } from 'react-native-flex-layout';
import { Feather } from '@expo/vector-icons';
import { useActionSheet } from '@expo/react-native-action-sheet';

import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import { useTranslation } from '../i18n';
/******************************************************************************

ChipSection is useful for short text input. It makes for easy addition and deletion on mobile devices.

useState Values:
  Chips: List of text to use as chip titles
  setChips: set this variable, will update in parent as well

Callbacks:
  deleteChip()
    notifies parent of deletions
  addChip()
    notifies parent of addition
  
Permissions:
  canDelete
    if true, allows users to edit KPIs
  canAdd
    if true, allows users to add more KPIs. Depending on if validValues is supplied or not, chips will be either free-form text input, or pre-determined values.
  doSynchronize
    if true, notify parent of changes so that parent can synchronize how they wish

Info:
  styles
    buttonAdd
    buttonDelete
    Chip
    ChipAdd
    ChipText
    ChipSelected
  validValues
    Valid Values for restricted chip input




******************************************************************************/

const ChipSection = (props) => {
  const { t } = useTranslation(['admin', 'common']);

  const [ChipDeleteButton, setChipDeleteButton] = useState(null);
  const [ChipAddButton, setChipAddButton] = useState(null);
  const [InputChip, setInputChip] = useState(null);

  const [Chips, setChips] = useState([]);

  const [ChipAddText, setChipAddText] = useState('');

  const [highlightedChips, setHighlightedChips] = useState([]);

  const { showActionSheetWithOptions } = useActionSheet();
  const hasMenu = Array.isArray(props.chipMenuActions) && props.chipMenuActions.length > 0;
  const [webMenuChip, setWebMenuChip] = useState(null);

  const openChipMenu = (element) => {
    const actions = props.chipMenuActions;

    // Web: native action sheet renders as a bottom-anchored popup that looks
    // out of place on desktop. Use a centered modal menu instead.
    if (isWeb()) {
      setWebMenuChip(element);
      return;
    }

    const labels = actions.map((a) => a.label);
    const options = [...labels, t('common:actions.cancel')];
    const cancelButtonIndex = options.length - 1;
    const destructiveButtonIndex = actions.findIndex((a) => a.destructive);

    showActionSheetWithOptions(
      {
        title: element,
        options,
        cancelButtonIndex,
        ...(destructiveButtonIndex >= 0 ? { destructiveButtonIndex } : {}),
      },
      (selectedIndex) => {
        if (selectedIndex == null || selectedIndex === cancelButtonIndex) return;
        const action = actions[selectedIndex];
        if (action && typeof action.onPress === 'function') {
          action.onPress(element);
        }
      },
    );
  };

  const handleWebMenuSelect = (action) => {
    const element = webMenuChip;
    setWebMenuChip(null);
    if (action && typeof action.onPress === 'function' && element != null) {
      action.onPress(element);
    }
  };

  //Run on start
  useEffect(() => {}, []);

  //Runs when Chips changes - show delete button for non-pill variants
  useEffect(() => {
    // Pill variant uses direct toggle, no delete button needed
    if (props.variant === 'pill') {
      setChipDeleteButton(() => null);
      return;
    }
    // Menu mode: tap opens an action sheet, no batch-delete button
    if (hasMenu) {
      setChipDeleteButton(() => null);
      return;
    }
    if (props.canDelete && highlightedChips.length > 0) {
      setChipDeleteButton(() => (
        <TouchableWithoutFeedback onPress={() => _deleteChips()}>
          <View style={props.styles?.buttonDelete}>
            <CustomText font="medium" size={15} color={'#FFFFFF'} style={{ textAlign: 'center' }}>
              {t('chips.deleteSelected')}
            </CustomText>
          </View>
        </TouchableWithoutFeedback>
      ));
    } else {
      setChipDeleteButton(() => null);
    }
  }, [highlightedChips, props.variant, props.styles, hasMenu]);

  //Runs when ChipAddText changes - for free text input mode (non-pill)
  useEffect(() => {
    // Pill variant uses restricted input chips, no add button needed
    if (props.variant === 'pill') {
      setChipAddButton(() => null);
      return;
    }
    if (props.canAdd && ChipAddText.length > 0) {
      setChipAddButton(() => (
        <TouchableWithoutFeedback onPress={() => addChip()}>
          <View style={props.styles?.buttonAdd}>
            <CustomText font="medium" size={15} color={'#FFFFFF'}>
              {t('chips.addTag')}
            </CustomText>
          </View>
        </TouchableWithoutFeedback>
      ));
    } else {
      setChipAddButton(() => null);
    }
  }, [ChipAddText, props.variant, props.styles]);

  useEffect(() => {
    if (props.canAdd) {
      var cut = [];
      const currentValues = props.Chips;
      const isPill = props.variant === 'pill';

      if (props.validValues) {
        props.validValues.forEach((value, index) => {
          if (currentValues.indexOf(value) == -1) {
            cut.push(value);
          }
        });
      }

      if (cut && cut.length > 0) {
        setInputChip(() =>
          cut.map((value, index) => (
            <Pressable
              key={index}
              onPress={() => handleRestrictedInputAdd(value)}
              style={({ pressed }) => [
                isPill ? componentStyles.pillChipAdd : props.styles.ChipAdd,
                pressed && { opacity: 0.8 },
              ]}
            >
              <CustomText
                font="medium"
                size={isPill ? 14 : 15}
                color={AppStyles.colorSet.textMuted}
              >
                {'+' + value}
              </CustomText>
            </Pressable>
          )),
        );
      } else if (!props.validValues) {
        setInputChip(() => (
          <Pressable style={isPill ? componentStyles.pillChipAdd : props.styles.ChipAdd}>
            <TextInput
              value={ChipAddText}
              onChangeText={(value) => setChipAddText(() => value)}
              onSubmitEditing={() => addChip()}
              blurOnSubmit={false}
              returnKeyType="done"
              placeholder={t('chips.addPlaceholder')}
              placeholderTextColor={'rgba(0,0,0,0.35)'}
              style={isPill ? { fontSize: 14 } : props.styles.ChipText}
            />
          </Pressable>
        ));
      } else {
        setInputChip(() => null);
      }
    }
  }, [props.validValues, props.Chips, ChipAddText, props.doSynchronize, props.variant]);

  //INTERNAL METHOD
  //Allows user to highlight chips for deletion
  const toggleChip = (element) => {
    if (props.canDelete) {
      if (highlightedChips.includes(element)) {
        var copy = [...highlightedChips];
        copy = copy.filter((e) => e != element);
        setHighlightedChips(() => copy);
      } else {
        var copy = [...highlightedChips];
        copy.push(element);
        setHighlightedChips(() => copy);
      }
    }
  };

  //INTERNAL METHOD - For pill variant: directly remove a chip on tap
  const removeChip = (element) => {
    if (props.canDelete) {
      const resultantList = props.Chips.filter((e) => e !== element);
      props.setChips(resultantList);
      if (props.doSynchronize) {
        props.deleteChips([element]);
      }
    }
  };

  //EXTERNAL METHOD
  //Add a chip to the internal state and update parent's state - Use restricted input mode
  const handleRestrictedInputAdd = (text) => {
    if (props.canAdd) {
      if (text == '' || text.length <= 1) {
        return null;
      }

      var chips = [...props.Chips];

      chips.push(text);
      if (props?.sortChips) {
        chips = chips.sort();
      }

      props.setChips(() => chips); //update state of Chips object

      //notify parent
      if (props.doSynchronize) {
        props.addChip(text); //Call parent implementation for adding Chip
      }
    }
  };

  //EXTERNAL METHOD
  //Adds a chip to the internal state and update's parent's state.
  const addChip = async () => {
    if (props.canAdd) {
      let text = ChipAddText;
      setChipAddText(() => ''); //Immediately remove to avoid duplication
      setChipAddButton(() => null); //Immediately remove for same reason

      // Remove leading and trailing spaces. Don't add blank tags.
      text = text.trim();
      var chips = props.Chips;
      if (text == '' || text.length <= 1 || chips.includes(text)) {
        return null;
      }

      //If text is not empty, add new chip
      chips.push(text);
      if (props?.sortChips) {
        chips = chips.sort();
      }

      props.setChips(chips); //update state of Chips object

      //notify parent
      if (props.doSynchronize) {
        props.addChip(text); //Call parent implementation for adding Chip
      }
    }
  };

  //EXTERNAL METHOD
  /*
  _deleteChips

  if deletion privaledges are granted, update child and parent state by deleting highlighted chips

  */
  const _deleteChips = () => {
    if (props.canDelete) {
      const resultantList = props.Chips.filter((e) => !highlightedChips.includes(e));
      const deletedList = props.Chips.filter((e) => highlightedChips.includes(e));

      props.setChips(resultantList);
      setHighlightedChips(() => []);
      //notify of exact change
      if (props.doSynchronize) {
        props.deleteChips(deletedList);
      }
    }
  };

  // Determine if using V3 pill variant
  const isPillVariant = props.variant === 'pill';

  // Determine if this is an AI chip section (shows sparkle icon)
  const isAIChips = props.isAI === true;

  // Get chip styles based on variant
  // For pill variant: chips in props.Chips are always "selected" (gold)
  // highlightedChips is for delete mode selection (different from being selected)
  const getChipStyle = (element) => {
    const isHighlighted = highlightedChips.includes(element);
    if (isPillVariant) {
      // In pill variant, all chips in props.Chips are shown as selected (gold)
      // unless we're in delete mode and they're highlighted for deletion
      return [componentStyles.pillChip, componentStyles.pillChipSelected];
    }
    return isHighlighted ? props.styles.ChipSelected : props.styles.Chip;
  };

  const getChipTextColor = () => {
    if (isPillVariant) {
      // All chips in the list are selected, so use black text
      return '#000000';
    }
    if (isAIChips) {
      // AI chips use darker text for contrast on gold-tinted background
      return AppStyles.colorSet.textPrimary;
    }
    return '#000';
  };

  return (
    <View style={[componentStyles.container, (isPillVariant || isAIChips) && { backgroundColor: 'transparent' }]}>
      <Wrap shouldWrapChildren="true" spacing={isPillVariant ? 8 : 5}>
        {/* Chip Chips  */}
        {props.Chips.length == 0
          ? null
          : props.Chips.map((element, index) => {
              return (
                <Pressable
                  key={index}
                  testID="chip-box"
                  onPress={() => {
                    // Menu mode: open an action sheet for this chip
                    if (hasMenu) {
                      openChipMenu(element);
                      return;
                    }
                    // Pill variant: directly remove chip on tap (toggle behavior)
                    if (isPillVariant && props.canDelete) {
                      removeChip(element);
                      return;
                    }
                    // Non-pill variant: highlight for batch deletion
                    if (
                      !props.maxSelect ||
                      (props.maxSelect >= highlightedChips.length &&
                        highlightedChips.includes(element)) ||
                      (props.maxSelect > highlightedChips.length &&
                        !highlightedChips.includes(element))
                    ) {
                      toggleChip(element);
                    }
                  }}
                  style={({ pressed, hovered, focused }) => [
                    getChipStyle(element),
                    { cursor: 'pointer' },
                    (hovered || focused) && { opacity: 0.85 },
                    pressed && { opacity: 0.7 },
                    isAIChips && { flexDirection: 'row', alignItems: 'center' },
                  ]}
                >
                  {isAIChips && (
                    <Feather
                      name="zap"
                      size={14}
                      color={AppStyles.colorSet.brandPrimary}
                      style={{ marginLeft: 12 }}
                    />
                  )}
                  <CustomText
                    style={isPillVariant ? componentStyles.pillChipText : props.styles?.ChipText}
                    font="medium"
                    size={isPillVariant ? 14 : 15}
                    color={getChipTextColor()}
                  >
                    {element}
                  </CustomText>
                </Pressable>
              );
            })}
        {props.canAdd && InputChip}
      </Wrap>
      {props.canDelete && ChipDeleteButton}
      {props.canAdd && ChipAddButton}
      {hasMenu && (
        <Modal
          visible={webMenuChip != null}
          transparent
          animationType="fade"
          onRequestClose={() => setWebMenuChip(null)}
        >
          <Pressable style={componentStyles.menuOverlay} onPress={() => setWebMenuChip(null)}>
            <Pressable style={componentStyles.menuModal} onPress={(e) => e.stopPropagation()}>
              <CustomText
                font="semiBold"
                size={16}
                color={AppStyles.colorSet.textPrimary}
                style={componentStyles.menuTitle}
              >
                {webMenuChip}
              </CustomText>
              {(props.chipMenuActions ?? []).map((action, idx) => (
                <Pressable
                  key={idx}
                  style={({ pressed, hovered, focused }) => [
                    componentStyles.menuItem,
                    (hovered || focused) && { backgroundColor: AppStyles.stateSet.hover },
                    pressed && { backgroundColor: AppStyles.colorSet.inputBackground },
                  ]}
                  onPress={() => handleWebMenuSelect(action)}
                  accessibilityRole="button"
                  accessibilityLabel={action.label}
                >
                  <CustomText
                    font="medium"
                    size={15}
                    color={
                      action.destructive
                        ? AppStyles.colorSet.error
                        : AppStyles.colorSet.textPrimary
                    }
                  >
                    {action.label}
                  </CustomText>
                </Pressable>
              ))}
              <Pressable
                style={({ pressed, hovered, focused }) => [
                  componentStyles.menuItem,
                  componentStyles.menuCancel,
                  (hovered || focused) && { backgroundColor: AppStyles.stateSet.hover },
                  pressed && { backgroundColor: AppStyles.colorSet.inputBackground },
                ]}
                onPress={() => setWebMenuChip(null)}
                accessibilityRole="button"
                accessibilityLabel={t('common:actions.cancel')}
              >
                <CustomText font="medium" size={15} color={AppStyles.colorSet.textSecondary}>
                  {t('common:actions.cancel')}
                </CustomText>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      )}
    </View>
  );
};

const componentStyles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    flex: 1,
    flexBasis: 'content',
  },
  // V3 Pill variant styles - colors match ChipStyles.js
  pillChip: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    backgroundColor: '#F5F5F5',
  },
  pillChipSelected: {
    backgroundColor: '#FFC451',
    borderColor: '#FFC451',
    borderWidth: 1,
  },
  pillChipText: {
    // Base text style - color handled inline
  },
  pillChipAdd: {
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 20,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#E5E5E5',
    backgroundColor: '#F5F5F5',
  },
  // Web-only chip action menu (mobile uses native action sheet)
  menuOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  menuModal: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 12,
    paddingVertical: 8,
    minWidth: 280,
    maxWidth: 360,
    width: '100%',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
        elevation: 8,
      },
    }),
  },
  menuTitle: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.cardBorder,
    marginBottom: 4,
  },
  menuItem: {
    paddingHorizontal: 20,
    paddingVertical: 14,
  },
  menuCancel: {
    borderTopWidth: 1,
    borderTopColor: AppStyles.colorSet.cardBorder,
    marginTop: 4,
  },
});

export default ChipSection;
